import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { LogIn, LogOut, PauseCircle, PlayCircle, CheckCircle2, AlertTriangle, ShieldCheck, Info } from "lucide-react";
import moment from "moment";
import "moment/locale/ca";
import {
  getJourneyState, getSuggestedActions, calcWorkedMinutes,
  minutesToHHMM, getTypeLabel, STATE_CONFIG, ACTION_CONFIG, JOURNEY_STATES
} from "./journeyEngine";
moment.locale("ca");

const TYPE_ICONS = { entry: LogIn, exit: LogOut, pause_start: PauseCircle, pause_end: PlayCircle };
const ACTION_TYPE_COLORS = {
  entry: "text-emerald-600 bg-emerald-50",
  exit: "text-rose-600 bg-rose-50",
  pause_start: "text-amber-600 bg-amber-50",
  pause_end: "text-blue-600 bg-blue-50",
};

export default function ClockPanel({ venue, user }) {
  const [now, setNow] = useState(moment());
  const [todayRecords, setTodayRecords] = useState([]);
  const [submitting, setSubmitting] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [showLegal, setShowLegal] = useState(false);
  const [workedLive, setWorkedLive] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setNow(moment()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!venue || !user) return;
    loadTodayRecords();
  }, [venue?.id, user?.email]);

  // Live worked time counter
  useEffect(() => {
    const interval = setInterval(() => {
      setWorkedLive(calcWorkedMinutes(todayRecords.filter(r => !r.is_deleted)));
    }, 15000);
    return () => clearInterval(interval);
  }, [todayRecords]);

  useEffect(() => {
    setWorkedLive(calcWorkedMinutes(todayRecords.filter(r => !r.is_deleted)));
  }, [todayRecords]);

  const loadTodayRecords = async () => {
    const today = moment().format("YYYY-MM-DD");
    const yesterday = moment().subtract(1, "day").format("YYYY-MM-DD");
    const midnightToday = moment(today).startOf("day");
    const recs = await hosys.entities.TimeRecord.filter({ venue_id: venue.id, user_email: user.email });
    // Include yesterday's records for night shift support:
    // if shift is still open OR exit happened past midnight
    const yesterdayRecs = recs.filter(r => r.date === yesterday && !r.is_deleted);
    const hasNightShift = (() => {
      const sorted = [...yesterdayRecs].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      if (!sorted.length) return false;
      const last = sorted[sorted.length - 1];
      // Shift still open
      if (last.type === "entry" || last.type === "pause_start" || last.type === "pause_end") return true;
      // Exit happened past midnight (timestamp is today)
      if (last.type === "exit" && moment(last.timestamp).isSameOrAfter(midnightToday)) return true;
      return false;
    })();
    const todayRecs = recs.filter(r => r.date === today);
    const combined = hasNightShift
      ? [...yesterdayRecs, ...todayRecs]
      : todayRecs;
    setTodayRecords(combined.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)));
  };

  const handleClock = async (type) => {
    if (submitting) return;
    setSubmitting(type);
    const ts = new Date().toISOString();
    await hosys.entities.TimeRecord.create({
      venue_id: venue.id,
      user_email: user.email,
      user_name: user.full_name || user.email,
      type,
      timestamp: ts,
      date: moment().format("YYYY-MM-DD"),
      source: "web",
    });
    const clockedTime = moment().format("HH:mm");
    setConfirmation({ type, time: clockedTime });
    setTimeout(() => setConfirmation(null), 3000);
    await loadTodayRecords();
    setSubmitting(null);
  };

  const journey = getJourneyState(todayRecords.filter(r => !r.is_deleted));
  const { primary, secondary } = getSuggestedActions(journey);
  const cfg = journey.state !== JOURNEY_STATES.NOT_STARTED ? STATE_CONFIG[journey.state] : null;

  if (!venue) {
    return (
      <div className="text-center py-16 text-gray-400">
        <p>Sense local assignat</p>
      </div>
    );
  }

  // All possible actions in order
  const ALL_ACTIONS = [
    { type: "entry", label: "ENTRADA", icon: LogIn, color: "bg-emerald-500 hover:bg-emerald-600" },
    { type: "pause_start", label: "PAUSA", icon: PauseCircle, color: "bg-amber-400 hover:bg-amber-500" },
    { type: "pause_end", label: "FI PAUSA", icon: PlayCircle, color: "bg-blue-500 hover:bg-blue-600" },
    { type: "exit", label: "SORTIDA", icon: LogOut, color: "bg-rose-500 hover:bg-rose-600" },
  ];

  return (
    <div className="max-w-lg mx-auto space-y-4">
      {/* Header card */}
      <div className="bg-[#7B2D3F] rounded-2xl p-6 text-white text-center shadow-lg">
        {cfg && (
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className={`w-2 h-2 rounded-full animate-pulse ${cfg.dot}`} />
            <p className="text-sm font-medium text-white/80">{cfg.label}</p>
            {workedLive > 0 && <span className="text-xs text-white/50 ml-1">· {minutesToHHMM(workedLive)}</span>}
          </div>
        )}
        <p className="text-6xl font-bold font-mono tabular-nums tracking-tight leading-none">
          {now.format("HH:mm")}
          <span className="text-3xl text-white/50">:{now.format("ss")}</span>
        </p>
        <p className="text-white/50 mt-2 text-sm capitalize">{now.format("dddd, D MMMM YYYY")}</p>
        <p className="text-white/90 font-semibold text-lg mt-3">{user?.full_name || user?.email}</p>
        <p className="text-white/40 text-xs mt-0.5">{venue?.name}</p>

        {journey.entryTime && (
          <p className="text-white/60 text-xs mt-2">
            Entrada: {moment(journey.entryTime).format("HH:mm")}
          </p>
        )}
      </div>

      {/* Confirmation */}
      {confirmation && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center gap-3 shadow-sm">
          <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
            <CheckCircle2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="font-bold text-emerald-800 text-base">{ACTION_CONFIG[confirmation.type]?.confirmMsg}</p>
            <p className="text-emerald-600 text-sm">a les {confirmation.time} · {venue?.name}</p>
          </div>
        </div>
      )}

      {/* Issues */}
      {journey.issues.length > 0 && !confirmation && (
        <div className="space-y-2">
          {journey.issues.map((issue, i) => (
            <div key={`issue-${i}`} className={`flex items-center gap-2.5 rounded-xl p-3 border text-sm font-medium ${issue.severity === "error" ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              {issue.msg}
            </div>
          ))}
        </div>
      )}

      {/* Inconsistent state */}
      {journey.state === JOURNEY_STATES.INCONSISTENT && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-center">
          <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="font-bold text-red-800">Jornada amb incidències</p>
          <p className="text-sm text-red-600 mt-1">Contacta amb el supervisor per corregir el teu registre.</p>
        </div>
      )}

      {/* Smart action buttons */}
      {journey.state !== JOURNEY_STATES.INCONSISTENT && (
        <>
          {/* Primary suggested action (big) */}
          {primary && (
            <button
              onClick={() => handleClock(primary)}
              disabled={!!submitting}
              className={`w-full ${ACTION_CONFIG[primary].color} text-white rounded-2xl py-6 flex flex-col items-center gap-2 transition-all active:scale-95 disabled:opacity-50 shadow-lg`}
            >
              <span className="font-bold text-2xl tracking-wide">{ACTION_CONFIG[primary].label}</span>
              {submitting === primary && <span className="text-sm text-white/70">Registrant...</span>}
            </button>
          )}

          {/* Secondary action */}
          {secondary && (
            <button
              onClick={() => handleClock(secondary)}
              disabled={!!submitting}
              className="w-full bg-white border-2 border-gray-200 text-gray-600 rounded-2xl py-4 flex items-center justify-center gap-2 transition-all active:scale-95 hover:border-gray-300 font-semibold"
            >
              {ACTION_CONFIG[secondary].label}
            </button>
          )}

          {/* All actions (compact, secondary) */}
          <div className="grid grid-cols-4 gap-2">
            {ALL_ACTIONS.map(({ type, label, icon: Icon, color }) => {
              const isPrimary = type === primary;
              const isSec = type === secondary;
              if (isPrimary || isSec) return null;
              return (
                <button
                  key={type}
                  onClick={() => handleClock(type)}
                  disabled={!!submitting}
                  className={`${color} active:bg-opacity-80 text-white rounded-xl py-3 flex flex-col items-center gap-1.5 transition-all active:scale-95 disabled:opacity-30 text-center opacity-50`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-semibold text-[10px]">{label}</span>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Today's records */}
      {todayRecords.filter(r => !r.is_deleted).length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-700">Registres d'avui</p>
            {workedLive > 0 && <span className="text-xs font-bold text-[#7B2D3F]">{minutesToHHMM(workedLive)} treballades</span>}
          </div>
          <div className="divide-y divide-gray-50">
            {todayRecords.filter(r => !r.is_deleted).map(r => {
              const Icon = TYPE_ICONS[r.type] || LogIn;
              return (
                <div key={r.id} className="flex items-center gap-3 px-4 py-3">
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${ACTION_TYPE_COLORS[r.type]}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium text-gray-700 flex-1">{getTypeLabel(r.type)}</span>
                  <span className="text-sm font-mono font-bold text-gray-800">{moment(r.timestamp).format("HH:mm")}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Legal footer */}
      <div className="flex items-center justify-center gap-1.5 pt-1">
        <button onClick={() => setShowLegal(!showLegal)} className="flex items-center gap-1 text-[10px] text-gray-300 hover:text-gray-400 transition-colors">
          <ShieldCheck className="w-3 h-3" />
          Registre oficial RD-Llei 8/2019
          <Info className="w-3 h-3" />
        </button>
      </div>
      {showLegal && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-xs text-blue-700">
          Conforme al <strong>Reial Decret-llei 8/2019</strong>, tots els fitxatges es registren de forma fiable amb data, hora i treballador. Es conserven durant <strong>4 anys</strong>. Les dades es tracten conforme al <strong>RGPD</strong>.
        </div>
      )}
    </div>
  );
}